// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MarketoForm from "@vanmeter/App/Components/Common/MarketoForm";
import { fontConfig } from "@vanmeter/XDL/constants";
import { color, xScreen } from "@vanmeter/XDL/Utilities";
import React, { FC, useContext, useRef, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    eventName?: string;
    eventLocation?: string;
    eventDate?: string;
}

export interface RequestYourSpotStyles {
    container?: InjectableCss;
    titleText?: TypographyProps;
    descriptionText?: TypographyProps;
    loadingOverlay?: InjectableCss;
    submitButton?: ButtonPresentationProps;
}

export const requestYourSpotStyles: RequestYourSpotStyles = {
    container: {
        css: css`
            background-color: ${color("common.ui14")};
            border: 1px solid ${color("common.border")};
            padding: 12px;
            border-radius: 4px;
            max-width: 450px;
            height: fit-content;
            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
        `,
    },
    titleText: {
        variant: "h4",
    },
    submitButton: {
        css: css`
            width: 100%;
        `,
    },
    descriptionText: {
        ...fontConfig.small,
        css: css`
            a,
            > p {
                font-size: ${fontConfig.small.size}px;
                line-height: ${fontConfig.small.lineHeight};
            }
        `,
    },
};

const styles = requestYourSpotStyles;

const config = {
    formId: 1029,
    munchkinId: "745-BOQ-621",
    baseUrl: "//go.vanmeterinc.com",
};

const RequestYourSpot: FC<OwnProps> = ({ eventName, eventLocation, eventDate }) => {
    const [isLoading, setIsLoading] = useState(false);
    const formRef = useRef<any>(null);
    const toasterContext = useContext(ToasterContext);

    const onFormSuccess = () => {
        toasterContext.addToast({ body: siteMessage("VM_RequestYourSpotSuccessMessage"), messageType: "success" });
        setIsLoading(false);
    };

    const hiddenFields = {
        new_web_page_url: document?.location?.href,
        new_web_pagetitle: document?.title,
        new_web_event_name: eventName,
        new_web_event_location: eventLocation,
        new_web_event_date: eventDate,
    };

    const handleSubmit = () => {
        const form = formRef.current;
        if (!form) {
            return;
        }

        const isValidForm = form.validate?.();
        if (!isValidForm) {
            return;
        }

        setIsLoading(true);
        form.submit?.();
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.titleText}>{translate("Request a Training Quote")}</Typography>
            <Typography {...styles.descriptionText}>{siteMessage("Request_Details_message")}</Typography>
            <LoadingOverlay loading={isLoading} {...styles.loadingOverlay}>
                <MarketoForm formRef={formRef} onSuccess={onFormSuccess} hiddenFields={hiddenFields} {...config} />
                <Button
                    disabled={isLoading}
                    onClick={handleSubmit}
                    aria-label={translate("Submit for Quote")}
                    {...styles.submitButton}
                >
                    {translate("Submit for Quote")}
                </Button>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

export default RequestYourSpot;
